import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { Injector } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http';
import { AirportCatalogService } from 'src/app/core/services/airport-catalog/airport-catalog.service';
import { DestinationImageResolver } from '../flight-ticket-frame/destination-image.resolver';
import { CityLookup } from '../flight-ticket-frame/flight-ticket-frame.component';
import { HeaderSchedule, ItinerarySummary, headerSchedule, summarizeCardItinerary, toLegs } from '../flight-ticket-frame/flight-ticket.util';
import { ChooseFlightData } from 'src/app/flight/models/ChooseFlightData';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { Confirmation } from 'src/app/flight/models/Confirmation';
import { getItemFromSessionStorage, getInstanceFromSessionStorage, FLIGHT_FORM_DATA, CHOOSE_FLIGHT_DATA, removeItensFromSessionStorage, GOOGLE_USER, CREW_INFO } from 'src/app/utils/SessionStorageUtil';
import { CrewInfoStore } from 'src/app/core/services/crew-info/crew-info.store';
import { FlightSegment, NewTicketRequest } from '../models/NewTicketRequest';
import { PasseLivreService } from 'src/app/core/services/passe-livre/passe-livre.service';
import { NewTicketResponse } from '../models/NewTicketResponse';
import { DialogInfoComponent } from 'src/app/shared/components/dialog-info/dialog-info.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { finalize } from 'rxjs/operators';
import { CrewInfo } from 'src/app/models/CrewInfo';
import { PasseLivreExternalCrewService } from 'src/app/core/services/passe-livre/passe-livre-external-crew.service';
import { AuthorizationService } from 'src/app/core/services/authorization/authorization.service';
import { PasseLivreLatamCrewService } from 'src/app/core/services/passe-livre/passe-livre-latam-crew.service';
import { RouterPasseLivre } from 'src/app/core/services/router/router';
import { RouterExternalCrewService } from 'src/app/core/services/router/router-external-crew.service';
import { RouterLatamCrewService } from 'src/app/core/services/router/router-latam-crew.service';
import { HomeComponent } from 'src/app/pages/home/home.component';
import { GoogleUser } from 'src/app/models/GoogleUser';
import { GoogleAuthenticationService } from 'src/app/core/services/login/google-authentication.service';
import { FlightSegmentSch, OTAAirScheduleResponse, OriginDestinationOption } from '../models/OTAAirScheduleResponse';

/** One direction of the trip, prepared for the ticket frame (presentation only). */
export interface ConfirmationLegView {
  direction: 'IDA' | 'VOLTA';
  option: OriginDestinationOption;
  itinerary: ItinerarySummary;
  schedule: HeaderSchedule;
  /** "LA3565 / LA4676": every real segment, in order. */
  flightNumbers: string;
  /** Departure of the first segment, for the date of the leg. */
  departure: string;
}

@Component({
    selector: 'app-flight-confirmation',
    templateUrl: './flight-confirmation.component.html',
    styleUrls: ['./flight-confirmation.component.scss'],
    standalone: false
})
export class FlightConfirmationComponent implements OnInit {

  private passeLivreService: PasseLivreService;
  private routerService: RouterPasseLivre;
  // TODO: componente undefined
  private homeComponente: HomeComponent;

  constructor(public dialog: MatDialog,
    private passeLivreServiceTwo: PasseLivreLatamCrewService,
    private googleAuthenticationService: GoogleAuthenticationService,
    public notificationService: NotificationService,
    private injector: Injector,
    private crewInfoStore: CrewInfoStore,
    private authorizationService: AuthorizationService) {

    if (this.authorizationService.isExternal()) {
      this.passeLivreService = <PasseLivreExternalCrewService>this.injector.get(PasseLivreExternalCrewService);
      this.routerService = <RouterExternalCrewService>this.injector.get(RouterExternalCrewService);
    } else {
      this.passeLivreService = <PasseLivreLatamCrewService>this.injector.get(PasseLivreLatamCrewService);
      this.routerService = <RouterLatamCrewService>this.injector.get(RouterLatamCrewService);
    }
  }

  @ViewChild('loading') templateLoadingRef: TemplateRef<any>;
  dialogLoadingRef: MatDialogRef<any>;

  confirmation: Confirmation = new Confirmation();

  // Presentation only: the trip summary reuses the search-ticket helpers and the destination
  // photo resolver. Nothing here feeds the reservation request.
  private readonly imageResolver = inject(DestinationImageResolver);
  private readonly airportCatalog = inject(AirportCatalogService);
  private legViews: ConfirmationLegView[] = [];
  private legSources: readonly (OriginDestinationOption | undefined)[] = [];
  /** The destination photo failed here; the frame-style fallback is shown instead. */
  imageFailed = false;

  /** City names travel with the selected segments; the airport catalog fills any gap. */
  readonly cityOf: CityLookup = iata => {
    for (const leg of this.legs) {
      for (const segment of leg.option.flightSegments ?? []) {
        if (segment.originLocationCode === iata && segment.originCityName) return segment.originCityName;
        if (segment.destinationLocationCode === iata && segment.destinationCityName) return segment.destinationCityName;
      }
    }
    return this.airportCatalog.cityName(iata);
  };

  ngOnInit(): void {
    this.imageResolver.prepare();
    this.init();

  }

  /** IDA, then VOLTA for a round trip. Rebuilt only when the selected options change. */
  get legs(): ConfirmationLegView[] {
    const sources = [this.confirmation.outbound, this.confirmation.isRoundTrip() ? this.confirmation.inbound : undefined];
    if (sources[0] !== this.legSources[0] || sources[1] !== this.legSources[1]) {
      this.legSources = sources;
      this.legViews = sources.flatMap((option, index) => {
        const segments = option?.flightSegments ?? [];
        if (!segments.length) return [];
        const legs = toLegs(segments);
        return [{
          direction: index === 0 ? 'IDA' : 'VOLTA',
          option: option as OriginDestinationOption,
          itinerary: summarizeCardItinerary(legs),
          schedule: headerSchedule(legs),
          flightNumbers: segments.map(segment => `${segment.operator || 'LA'}${segment.flightNumber}`).join(' / '),
          departure: segments[0].departureDateTime
        } as ConfirmationLegView];
      });
    }
    return this.legViews;
  }

  /**
   * Airport the trip goes to: the destination of the outbound leg (BSB → MAO → BSB shows MAO).
   * The inbound leg is never used, since its arrival is the home airport.
   */
  get mainDestination(): string | null {
    return this.legs[0]?.itinerary.destination ?? null;
  }

  /** "BSB → MAO" over the photo: origin of the outbound leg and the main destination. */
  get tripRoute(): string {
    const stops = this.legs[0]?.itinerary.stops ?? [];
    if (!stops.length) return '';
    const destination = this.mainDestination ?? stops[stops.length - 1];
    return stops[0] === destination ? stops.join(' → ') : `${stops[0]} → ${destination}`;
  }

  get imageUrl(): string | null {
    return this.imageResolver.resolve(this.mainDestination);
  }

  get imageAlt(): string {
    const destination = this.mainDestination;
    if (!destination) return '';
    return `Destino ${this.cityOf(destination) ?? destination}`;
  }

  /** Photo of the airport this direction really goes to: GRU → GIG shows GIG, GIG → GRU shows GRU. */
  legImageUrl(leg: ConfirmationLegView): string | null {
    return this.imageResolver.resolve(leg.itinerary.destination);
  }

  legImageAlt(leg: ConfirmationLegView): string {
    const destination = leg.itinerary.destination;
    if (!destination) return '';
    return `Destino ${this.cityOf(destination) ?? destination}`;
  }

  onImageError(url: string): void {
    this.imageFailed = true;
    this.imageResolver.reportLoadError(url);
  }

  async init() {
    let crewInfo = getItemFromSessionStorage<CrewInfo>(CREW_INFO);
    const flightFormData = getItemFromSessionStorage<FlightsFormData>(FLIGHT_FORM_DATA);
    const chooseFlightData = getInstanceFromSessionStorage(CHOOSE_FLIGHT_DATA, ChooseFlightData);

    if (crewInfo == null) {
      await this.getCrewInfo();
      crewInfo = getItemFromSessionStorage<CrewInfo>(CREW_INFO);
    }

    this.confirmation.flightOption = flightFormData ? flightFormData.option : this.confirmation.flightOption;
    this.confirmation.crewName = crewInfo ? crewInfo.firstName : this.confirmation.crewName;
    this.confirmation.outbound = chooseFlightData ? chooseFlightData.outbound : this.confirmation.outbound;
    this.confirmation.inbound = chooseFlightData ? chooseFlightData.inbound : this.confirmation.inbound;
  }

  back(): void {
    this.routerService.goHome({ tab: 1 });
  }

  async getCrewInfo() {
    return new Promise<boolean>((resolve) => {
      const googleUser: GoogleUser | null = this.googleAuthenticationService.getGoogleUser();

      if (googleUser?.email) {
        this.passeLivreServiceTwo.getLatamCrew().subscribe({
          next: (crewInfo: CrewInfo) => {
            this.crewInfoStore.setCrewInfo(crewInfo);
            resolve(true);
          },
          error: () => {
            this.notificationService.error('Ocorreu um erro ao buscar os dados do tripulante.');
            resolve(false);
          }
        });
      }
    })
  }

  addSecondsToDateTime(dateTime: string): string {
    return dateTime.length === 16 ? dateTime + ':00' : dateTime;
}

  async confirmReservation() {
    let crewInfo = getItemFromSessionStorage<CrewInfo>(CREW_INFO);
    if (crewInfo == null) {
      await this.getCrewInfo();
      crewInfo = getItemFromSessionStorage<CrewInfo>(CREW_INFO);
    }

    const newTicketRequest: NewTicketRequest = this.createNewTicketRequest(crewInfo);
    
    if (newTicketRequest.outboundFlight && newTicketRequest.outboundFlight.length > 0) {
      newTicketRequest.outboundFlight.forEach(flight => {
          flight.departureDateTime = this.addSecondsToDateTime(flight.departureDateTime);
          flight.arrivalDateTime = this.addSecondsToDateTime(flight.arrivalDateTime);
      });
  }
  

  if (newTicketRequest.inboundFlight && newTicketRequest.inboundFlight.length > 0) {
      newTicketRequest.inboundFlight.forEach(flight => {
          flight.departureDateTime = this.addSecondsToDateTime(flight.departureDateTime);
          flight.arrivalDateTime = this.addSecondsToDateTime(flight.arrivalDateTime);
      });
  }
  

    this.startLoading();

    this.passeLivreService.newTicket(newTicketRequest)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (newTicketResponse: NewTicketResponse) => {
          if (newTicketResponse.success) {
            removeItensFromSessionStorage([FLIGHT_FORM_DATA, CHOOSE_FLIGHT_DATA]);
            this.routerService.goHome({ refresh: true });
            this.notificationService.success('Reserva realizada com sucesso!');
          } else {
            this.showErrors(newTicketResponse.errors);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.notificationService.error(this.resolveErrorMessage(err));
        }
      });
  }

  private startLoading(): void {
    this.dialogLoadingRef = this.dialog.open(this.templateLoadingRef, {
      width: '300px',
      disableClose: true,
      id: 'loading'
    });
  }

  private stopLoading(): void {
    this.dialogLoadingRef.close();
  }

  private createNewTicketRequest(crewInfo: CrewInfo | null): NewTicketRequest {
    const newTicketRequest = new NewTicketRequest();

    if (this.authorizationService.isExternal()) {
      console.log('crewInfo', crewInfo);
      newTicketRequest.code = crewInfo?.code?.value;
      newTicketRequest.email = crewInfo?.email;
    }
    newTicketRequest.crewCompany = crewInfo?.company ? crewInfo?.company : 'JJ';
    newTicketRequest.outboundFlight = this.createFlightSegment(this.confirmation.outbound);

    if (this.confirmation.isRoundTrip() && this.confirmation.inbound) {
      newTicketRequest.inboundFlight = this.createFlightSegment(this.confirmation.inbound);
    }

    return newTicketRequest;
  }

  private createFlightSegment(flight: OriginDestinationOption): FlightSegment[] {

    let flightSegmentList: FlightSegment[] = [];
    
    flight.flightSegments.forEach((segment: FlightSegmentSch) => {
      const flightSegment: FlightSegment = new FlightSegment();
      flightSegment.airlineCode = segment.operator;
      flightSegment.departureAirport = segment.originLocationCode;
      flightSegment.arrivalAirport = segment.destinationLocationCode;
      flightSegment.departureDateTime = segment.departureDateTime;
      flightSegment.arrivalDateTime = segment.arrivalDateTime;
      flightSegment.flightNumber = segment.flightNumber;
      flightSegmentList.push(flightSegment)
    });


    return flightSegmentList;

  }

  private static readonly BACKEND_ERROR_MESSAGES: Record<string, string> = {
    'Cannot verify block status for anacCode': 'Não foi possível verificar se o tripulante possui algum bloqueio no momento. Tente novamente mais tarde.',
    'Cannot verify block status for bp': 'Não foi possível verificar se o tripulante possui algum bloqueio no momento. Tente novamente mais tarde.',
  };

  private resolveErrorMessage(err: HttpErrorResponse): string {
    const body = typeof err.error === 'string' ? err.error.trim() : null;
    if (body && body in FlightConfirmationComponent.BACKEND_ERROR_MESSAGES) {
      return FlightConfirmationComponent.BACKEND_ERROR_MESSAGES[body];
    }
    return 'Houve um erro inesperado na confirmação da reserva!';
  }

  private showErrors(errors: string[]): void {
    const html: string = `
      <ul class="error-message">
        ${errors.map(error => `<li>${error}</li>`).join('')}
      </ul>
    `;

    this.dialog.open(DialogInfoComponent, {
      width: '600px',
      data: { title: 'Ocorreu um erro!', html: html }
    });
  }
}
